import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import axios from "axios"; 

function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "", 
    address: "",
    city: "",
    pincode: "",
    paymentMethod: "ONLINE" 
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [txnRef, setTxnRef] = useState("");

  // Generate a unique tracking transaction reference once when the component mounts
  useEffect(() => {
    setTxnRef(`ORD${Date.now()}`);
  }, []);

  // --- Calculations ---
  const subtotal = getCartTotal();
  const totalUnits = cart.reduce((sum, item) => sum + item.quantity, 0);
  const isDiscountEligible = totalUnits >= 2;
  
  const discount = isDiscountEligible ? Math.round(subtotal * 0.1 * 10) / 10 : 0;
  const finalAmount = Math.round((subtotal - discount) * 10) / 10;

  // --- Direct UPI QR & Intent Config ---
  const upiId = "eazypay.U1JUG0612WP7VOG@icici";
  const businessName = "CHIRAYU MEDICOS";
  const formattedAmount = finalAmount.toFixed(2);
  
  // mc=5411 (Drug Stores/Pharmacies), tr & tid protect against app duplicate constraints
  const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(businessName)}&am=${formattedAmount}&cu=INR&mc=5411&tr=${txnRef}&tid=${txnRef}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(upiUrl)}`;

  const strikeThroughText = (text) => text.split('').map(char => char + '\u0336').join('');

  if (cart.length === 0) {
    return (
      <div className="container" style={{ padding: "120px 20px", textAlign: "center" }}>
        <h2>{t("checkout.validation.emptyCart")}</h2>
        <button className="btn-primary" onClick={() => navigate("/services")} style={{marginTop: "20px"}}>
          {t("home.viewAllProducts")}
        </button>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.phone.trim() || !formData.name.trim() || !formData.address.trim() || !formData.email.trim()) {
      setError("Please fill out all required fields, including Email Address.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      // 1. Store order data block securely inside LocalStorage so PaymentSuccess can read it for EmailJS
      const localOrderBackup = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        pincode: formData.pincode,
        items: cart.map(i => `${i.name} (Qty: ${i.quantity})`).join(", "),
        totalAmount: finalAmount.toFixed(1)
      };
      localStorage.setItem("chirayu_pending_order", JSON.stringify(localOrderBackup));

      // 2. Log configuration telemetry parameters to MongoDB database via our local backend route
      await axios.post("/api/order", { 
        name: formData.name,
        phone: formData.phone,
        email: "", // Empty string block preserves backend validation crashes
        address: formData.address,
        city: formData.city,
        pincode: formData.pincode,
        items: cart, 
        subtotal: parseFloat(subtotal.toFixed(1)), 
        discount: parseFloat(discount.toFixed(1)), 
        totalAmount: parseFloat(finalAmount.toFixed(1)),
        transactionId: txnRef // Informing the backend of the unified tracking number
      });

      // 3. Fallback Route Migration: Redirect cleanly straight over to the confirmation display
      navigate(`/payment-success?txnStatus=SUC&merchantTxnNo=${txnRef}&amount=${finalAmount.toFixed(1)}`);
    } catch (err) {
      console.error("Database tracking log exception caught:", err);
      // Operational Fallback: If your database fails, still allow the user to reach the success page to preserve sales
      navigate(`/payment-success?txnStatus=SUC&merchantTxnNo=${txnRef}&amount=${finalAmount.toFixed(1)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page contact-page">
      <section className="page-hero">
        <div className="section-label">{t("exclusive.label")}</div>
        <h1>{t("checkout.title")}</h1>
        <p>{t("checkout.subtitle")}</p>
      </section>

      <section className="section-block">
        <div className="container">
          <div className="contact-wrapper">
            
            {/* LEFT SIDE: SUMMARY CARD & MANUAL INLINE QR CODE */}
            <div className="contact-info">
              <div className="section-label" style={{ marginBottom: '10px' }}>{t("checkout.sections.summary")}</div>
              <h2 style={{ marginBottom: '30px' }}>{t("home.ourProducts")}</h2>
              
              <div style={{ marginTop: "20px" }}>
                {cart.map((item) => (
                  <div key={item.id} className="info-item" style={{ 
                    background: "#f1f3f5", 
                    padding: "15px 20px", 
                    borderRadius: "12px", 
                    marginBottom: "10px",
                    display: "block" 
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h4 style={{ color: "var(--primary-color)", margin: 0 }}>{item.name}</h4>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        style={{ background: "none", border: "none", color: "#ff4d4d", cursor: "pointer", fontSize: "0.8rem" }}
                      >
                        {t("exclusive.removeBtn")}
                      </button>
                    </div>
                    
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <button type="button" onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))} style={{ width: "30px", height: "30px", borderRadius: "4px", border: "1px solid #ddd" }}>-</button>
                        <span style={{ minWidth: "20px", textAlign: "center", fontWeight: "600" }}>{item.quantity}</span>
                        <button type="button" onClick={() => updateQuantity(item.id, item.quantity + 1)} style={{ width: "30px", height: "30px", borderRadius: "4px", border: "1px solid #ddd" }}>+</button>
                      </div>
                      <span style={{ fontWeight: "700", color: "var(--secondary-color)" }}>₹{(item.price * item.quantity).toFixed(1)}</span>
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: "20px", padding: "20px", borderTop: "2px solid #eee" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <span>Subtotal:</span>
                    <span>₹{subtotal.toFixed(1)}</span>
                  </div>
                  
                  {isDiscountEligible && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", color: "#28a745", fontWeight: "600" }}>
                      <span>{t("exclusive.offer")} (10% Off):</span>
                      <span>- ₹{discount.toFixed(1)}</span>
                    </div>
                  )}

                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "1.3rem", fontWeight: "bold", marginTop: "10px" }}>
                    <span>{t("checkout.table.total")}:</span>
                    <span style={{ color: "var(--primary-color)" }}>₹{finalAmount.toFixed(1)}</span>
                  </div>
                </div>
              </div>

              {/* UPI INTERFACE MODULE */}
              <div style={{ marginTop: "30px", padding: "20px", background: "#fff", borderRadius: "15px", textAlign: "center", border: "2px dashed #0d7a5f" }}>
                <div style={{ marginBottom: "20px" }}>
                  <a href={upiUrl} style={{
                    display: "inline-block",
                    padding: "12px 30px",
                    backgroundColor: "#0d7a5f",
                    color: "#fff",
                    textDecoration: "none",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.95rem"
                  }}>
                    📱 Pay via UPI App
                  </a>
                </div>
                
                <div style={{ margin: "15px 0", color: "#ccc", fontSize: "0.8rem" }}>— OR —</div>
                <p style={{ fontWeight: "700", marginBottom: "15px" }}>Scan to Pay Exact Amount</p>
                <img src={qrCodeUrl} alt="UPI QR" style={{ width: "180px", height: "180px", marginBottom: "10px" }} />
                <div style={{ fontSize: "1.4rem", fontWeight: "800", color: "#0d7a5f" }}>₹{finalAmount.toFixed(1)}</div>
                <div style={{ fontSize: "0.7rem", color: "#999", marginTop: "5px" }}>Ref ID: {txnRef}</div>
              </div>
            </div>

            {/* RIGHT SIDE: SHIPPING FORM DETAILS */}
            <div className="contact-form-box">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t("contact.form.yourName")}</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>{t("contact.form.phoneNumber")}</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="info@chirayumedicos.com" required />
                </div>
                <div className="form-group">
                  <label>{t("contact.address")}</label>
                  <textarea name="address" rows="2" value={formData.address} onChange={handleChange} required />
                </div>
                <div className="form-row" style={{ display: "flex", gap: "15px" }}>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>{t("contact.city")}</label>
                    <input type="text" name="city" value={formData.city} onChange={handleChange} required />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>{t("checkout.form.gst")}</label>
                    <input type="text" name="pincode" placeholder="313001" value={formData.pincode} onChange={handleChange} required />
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '10px' }}>
                  <label>{t("checkout.sections.payment")}</label>
                  <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} className="form-input">
                    <option value="ONLINE">Manual QR Code Scanner / UPI Intent</option>
                    <option value="COD" disabled style={{ color: "#bbb" }}>
                      {strikeThroughText(t("exclusive.codUnavailable"))}
                    </option>
                  </select>
                </div>
                
                {error && <p style={{ color: "red", fontSize: "0.8rem", marginTop: "10px" }}>{error}</p>}
                
                <button type="submit" className="btn-primary full-width" style={{ marginTop: "20px" }} disabled={loading}>
                  {loading ? "Registering Details..." : "I Have Scanned and Paid"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Checkout;